import React, { useState, useMemo } from "react";
import { ArrowRight, ArrowLeft } from "react-bootstrap-icons";
import noDataImg from "../../../assets/img/banner/no-data.png";

const TeamReport = ({ teamData }) => {
  console.log("🔍 [TeamReport] Received teamData:", teamData);
  
  const [isTeamInfo, setTeamInfo] = useState(false);
  const [level, setLevel] = useState(0);
  const [isTeamInfoData, setTeamInfoData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Transform the data from the new transformedData structure
  const levelData = useMemo(() => {
    if (!teamData?.transformedData?.team) {
      return [];
    }

    return teamData.transformedData.team.map(level => ({
      level: level.level,
      total_members: level.total_users_in_level,
      total_business: level.total_business,
      percentage: level.percentage,
      active_users: level.active_users_in_level,
      users: level.users || []
    }));
  }, [teamData]);

  const levelInfo = (levelNum) => {
    const teamInfo = levelData.find((data) => data.level === levelNum);
    
    setLevel(levelNum);
    setTeamInfoData(teamInfo ? teamInfo.users : []);
    setTeamInfo(true);
    setSearchTerm("");
    setSortConfig({ key: "", direction: "ascending" });
    setCurrentPage(1);
  };

  const handleFilter = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig((prevState) => ({
      key,
      direction:
        prevState.key === key && prevState.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
  };

  const filteredData = useMemo(() => {
    let filtered = isTeamInfo ? isTeamInfoData : levelData;

    if (searchTerm) {
      filtered = filtered.filter((val) =>
        [
          "level",
          "total_members",
          "total_business",
          "percentage",
          "active_users",
          "referral_id",
          "name",
          "email",
          "user_created_on",
          "first_package_activation_date"
        ].some((field) =>
          val[field]
            ?.toString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
      );
    }

    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aKey = a[sortConfig.key] || "";
        const bKey = b[sortConfig.key] || "";

        if (aKey < bKey) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aKey > bKey) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [isTeamInfo, isTeamInfoData, levelData, searchTerm, sortConfig]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const formatDate = (dateString) => {
    if (!dateString || dateString === "Not Active") return "Not Active";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const renderTable = (data, columns, showDetailsButton = false) => {
    return (
      <div className="table-responsive">
        <table className="table table-dark table-striped">
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th
                  key={index}
                  scope="col"
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                  style={col.sortable ? { cursor: 'pointer' } : {}}
                >
                  {col.label}
                  {sortConfig.key === col.key && (
                    <span className="ms-1">
                      {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((val, index) => (
              <tr key={index}>
                {showDetailsButton && (
                  <td>
                    <button
                      className="btn btn-info ms-2"
                      onClick={() => levelInfo(val.level)}
                      style={{ cursor: "pointer" }}
                    >
                      Level {val.level}
                    </button>
                  </td>
                )}
                {columns.slice(showDetailsButton ? 1 : 0).map((col, i) => (
                  <td key={i}>
                    {col.key === 'user_created_on' || col.key === 'first_package_activation_date' 
                      ? formatDate(val[col.key])
                      : val[col.key] !== undefined && val[col.key] !== null 
                        ? (col.key === 'total_business' && typeof val[col.key] === 'number'
                            ? `$${val[col.key].toFixed(2)}`
                            : val[col.key])
                        : 'N/A'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPagination = () => {
    if (filteredData.length <= itemsPerPage) return null;

    return (
      <div className="d-flex justify-content-between p-3">
        <button
          className="btn btn-primary"
          onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
          disabled={currentPage === 1}
        >
          <ArrowLeft />
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="btn btn-primary"
          onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          <ArrowRight />
        </button>
      </div>
    );
  };

  const teamLevelColumns = [
    { key: "level", label: "Level", sortable: true },
    { key: "total_members", label: "Total Users", sortable: true },
    { key: "active_users", label: "Active Users", sortable: true },
    { key: "total_business", label: "Total Business", sortable: true },
    { key: "percentage", label: "Percentage", sortable: true },
    { key: "button", label: "Details", sortable: false },
  ];

  const userColumns = [
    { key: "referral_id", label: "User ID", sortable: true },
    { key: "name", label: "Name", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "total_business", label: "Package Amount", sortable: true },
    { key: "active_package_count", label: "Active Packages", sortable: true },
    { key: "user_created_on", label: "Registration Date", sortable: true },
    { key: "first_package_activation_date", label: "Activation Date", sortable: true },
  ];

  return (
    <div>
      <div className="section-padding-medium">
        <div className="card mb-3">
          <div className="card-header">
            <div className="card-title bg-dark text-light text-center rounded p-3">
              <h5 className="m-0 me-2">
                <span className="me-2">
                  {!isTeamInfo ? "Generation Team Report" : `Level ${level} Details`}
                </span>
                {isTeamInfo && (
                  <button
                    className="btn btn-success ms-2 text-white"
                    onClick={() => {
                      setTeamInfo(false);
                      setSearchTerm("");
                      setCurrentPage(1);
                    }}
                  >
                    Back To Team Report
                  </button>
                )}
              </h5>
            </div>
          </div>

          <div className="card p-3">
            <input
              type="text"
              placeholder="Search by level, name, email, date..."
              value={searchTerm}
              onChange={handleFilter}
              className="mb-3 form-control"
            />

            {paginatedData.length > 0 ? (
              <>
                {!isTeamInfo
                  ? renderTable(paginatedData, teamLevelColumns, true)
                  : renderTable(paginatedData, userColumns, false)}
                {renderPagination()}
              </>
            ) : (
              <div className="no-data text-center rounded rounded-bottom">
                <img src={noDataImg} alt="No Data" className="img-fluid" />
                <p className="mt-2">
                  {levelData.length === 0 
                    ? "No team data available" 
                    : "No data matches your search criteria"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamReport;